import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Button from 'common/components/Elements/Button/Button';
import InputCheckbox from 'common/components/Elements/Form/InputCheckbox/InputCheckbox';
import InputSelect from 'common/components/Elements/Form/InputSelect/InputSelect';
import InputText, { DebouncedInputText } from 'common/components/Elements/Form/InputText/InputText';
import Icon from 'common/components/Elements/Icon/Icon';
import ModalActions from 'common/components/Elements/Modal/ModalActions/ModalActions';
import { ScrollableModalContent } from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import type { EvalFunctionWorkerMessage } from 'common/utilities/browser/webWorker/webWorker';
import { evalFunctionWorker } from 'common/utilities/browser/webWorker/webWorker';
import {
  arrayDiff,
  arrayEquals, arrayFilterOut, arraySymmetricDiff, arrayUnique,
} from 'common/utilities/core/array';
import { deepClone } from 'common/utilities/core/object';
import { isSectionResponse } from 'core/api/api.typesGuards';
import type * as I from 'core/api/coreApiClient';
import { deleteCommand,saveCommand, updateCommand } from 'core/redux/actions/command/commandActions';
import type {
  DeleteCommand,   SaveCommand,
UpdateTableColumnsVisibilityCommand,
} from 'core/redux/actions/command/commandActions.types';
import { enqueueInfoNotification } from 'core/redux/actions/notification/notificationActions';
import type {
  EnqueueNotification,
} from 'core/redux/actions/notification/notificationActions.types';
import { closeModalAction } from 'core/redux/actions/ui/uiActions';
import type { CloseModal } from 'core/redux/actions/ui/uiActions.types';
import type { AppDispatch, AppThunkDispatch } from 'core/redux/store/configureStore.types';
import { filterGroupInfos, filterGroupInfosWithQueryAndReturnMatchingIds } from 'core/utilities/table/filterGroupInfos';
import { availableColumnVisibilitySetsAsOptions, availableColumnVisibilitySetsAsOptionsGroups,
  hasGroupSelectedAll, metaInfoInfos } from 'core/utilities/table/table';
import theme, { ThemeColors } from 'core/utilities/theme';
import I18n from 'i18n/en-US';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import FilterGroupSelect from './FilterGroupSelect/FilterGroupSelect';
import Group from './Group/Group';

export const DEFAULT_SETTING_ID = '__active__';

export interface SaveCommandData {
  label: string;
  visiblePropertyIds: string[];
  shared?: boolean;
}

export interface ComponentProps {
  subHeader?: string;
  columnVisibilitySettings: I.ColumnVisibilitySettings;
  metaInfo: I.Group[];
  /** ids returned by the API, only used in setSelectedSet, then copied over the state to be manipulated */
  visiblePropertyIds: string[];
  onUpdate: (response: I.SectionResponse) => void;
  dispatchEnqueueInfoNotification: EnqueueNotification;
  dispatchSaveCommand: SaveCommand<SaveCommandData>;
  dispatchUpdateCommand: UpdateTableColumnsVisibilityCommand;
  dispatchDeleteCommand: DeleteCommand;
  dispatchCloseModalAction: CloseModal;
  maxShownColumns: number;
}

type SaveAsExpanded = 'new' | 'update' | undefined;

export interface ComponentState {
  filterQuery: string;
  /** Current group been opened (part of the filter query) */
  filterGroup?: I.Group;
  newSettingName: string;
  newSettingShared: boolean;
  visiblePropertyIds: string[];
  initialVisiblePropertyIds: string[];
  touchedPropertyIds: string[];
  selectedSet?: I.ColumnVisibilitySet;
  saveAsExpanded: SaveAsExpanded;
  persisting: boolean;
  calculateTouchedPropertyIdsWorker: Worker;
}

const applyText = (saveAsExpanded: SaveAsExpanded, selectedSet?: I.ColumnVisibilitySet) => {
  if (saveAsExpanded === 'new') {
    return I18n.Elements.Table.ModalColsVisibility.createAndApply;
  }

  if (saveAsExpanded === 'update') {
    if (selectedSet?.saveUpdateCommand) {
      return [
        { id: 'apply', text: I18n.Elements.Table.ModalColsVisibility.apply },
        { id: 'updateAndApply', text: I18n.Elements.Table.ModalColsVisibility.updateAndApply },
      ];
    } else {
      return [
        { id: 'apply', text: I18n.Elements.Table.ModalColsVisibility.apply },
      ];
    }
  }

  return I18n.Elements.Table.ModalColsVisibility.apply;
};

export class ModalColsVisibility extends Component<ComponentProps, ComponentState> {
  constructor(props: ComponentProps) {
    super(props);

    const calculateTouchedPropertyIdsWorker = evalFunctionWorker();
    calculateTouchedPropertyIdsWorker.onmessage = (e) => {
      if (e && e.data) {
        this.setState({
          touchedPropertyIds: e.data as string[],
        });
      }
    };

    this.state = {
      filterQuery: '',
      newSettingName: '',
      newSettingShared: false,
      initialVisiblePropertyIds: deepClone(props.visiblePropertyIds),
      visiblePropertyIds: [],
      touchedPropertyIds: [],
      persisting: false,
      saveAsExpanded: undefined,
      calculateTouchedPropertyIdsWorker,
    };
  }

  componentDidMount() {
    this.setSelectedSet();
  }

  componentDidUpdate(prevProps: ComponentProps, prevState: ComponentState) {
    const { visiblePropertyIds: propsVisiblePropertyIds } = this.props;
    const { visiblePropertyIds: stateVisiblePropertyIds } = this.state;

    // If the list of visible columns has been saved (deleted, updated)
    // the visiblePropertyIds will change and we will need to recalculate the default set
    if (!arrayEquals(prevProps.visiblePropertyIds, propsVisiblePropertyIds)) {
      this.setSelectedSet();
    }

    // Whenever the user toggle properties we need to re-calculate which one have been touched
    if (!arrayEquals(prevState.visiblePropertyIds, stateVisiblePropertyIds)) {
      this.evalCalculateTouchedPropertyIds(stateVisiblePropertyIds);
    }
  }

  /**
   * Calculating touched properties is instant on most of the table having a few hundreds columns.
   * But on very large table having 30k+ columns it might take 3seconds. As it is only an indication
   * to the user, we perform that task in a background worker to avoid the interface to freeze.
   */
  evalCalculateTouchedPropertyIds = (newVisiblePropertyIds: string[]) => {
    const { initialVisiblePropertyIds, calculateTouchedPropertyIdsWorker } = this.state;

    const workerMessage: EvalFunctionWorkerMessage = {
      dependentFunctions: [arrayDiff.toString()],
      evaluatedFunction: arraySymmetricDiff.toString(),
      evaluatedFunctionArgs: [initialVisiblePropertyIds, newVisiblePropertyIds],
    };

    calculateTouchedPropertyIdsWorker.postMessage(workerMessage);
  };

  handleNewSettingNameChange = (value: string) => {
    this.setState({ newSettingName: value });
  };

  handleNewSettingSharedChange = () => {
    this.setState((prevState) => ({ newSettingShared: !prevState.newSettingShared }));
  };

  handleApply = (id: string) => {
    const { saveAsExpanded } = this.state;

    if (saveAsExpanded === 'new') {
      this.handleSaveAsNew();
    } else if (saveAsExpanded === 'update' && id === 'updateAndApply') {
      this.handleSaveAsUpdate();
    } else {
      this.handleSave();
    }
  };

  handleSaveAsUpdate = async () => {
    const {
      dispatchUpdateCommand, onUpdate,
      dispatchEnqueueInfoNotification, dispatchCloseModalAction,
    } = this.props;
    const { visiblePropertyIds, selectedSet } = this.state;

    if (selectedSet && selectedSet.saveUpdateCommand) {
      this.setState({ persisting: true });

      const response = await dispatchUpdateCommand(selectedSet.saveUpdateCommand, {
        label: selectedSet.label,
        visiblePropertyIds,
        shared: selectedSet.shared,
      });

      if (isSectionResponse(response)) {
        dispatchEnqueueInfoNotification(I18n.Elements.Table.ModalColsVisibility.updateSuccess);
        onUpdate(response);
        dispatchCloseModalAction();
      } else {
        this.setState({ persisting: false });
      }
    }
  };

  handleSaveAsNew = async () => {
    const {
      columnVisibilitySettings, dispatchSaveCommand, dispatchCloseModalAction, onUpdate,
      dispatchEnqueueInfoNotification,
    } = this.props;
    const { visiblePropertyIds, newSettingName, newSettingShared } = this.state;

    if (columnVisibilitySettings.saveNewCommand) {
      this.setState({ persisting: true });

      const response = await dispatchSaveCommand(columnVisibilitySettings.saveNewCommand, {
        label: newSettingName,
        visiblePropertyIds,
        shared: newSettingShared,
      });

      if (isSectionResponse(response)) {
        dispatchEnqueueInfoNotification(I18n.Elements.Table.ModalColsVisibility.saveNewSuccess);
        onUpdate(response);
        dispatchCloseModalAction();
      } else {
        this.setState({ persisting: false });
      }
    }

    return Promise.resolve();
  };

  handleSave = async () => {
    const {
      columnVisibilitySettings, dispatchUpdateCommand, dispatchCloseModalAction, onUpdate,
      dispatchEnqueueInfoNotification,
    } = this.props;
    const { visiblePropertyIds } = this.state;

    if (columnVisibilitySettings.applyCommand) {
      this.setState({ persisting: true });

      const response = await dispatchUpdateCommand(columnVisibilitySettings.applyCommand, {
        visiblePropertyIds,
      });

      if (isSectionResponse(response)) {
        dispatchEnqueueInfoNotification(I18n.Elements.Table.ModalColsVisibility.saveSuccess);
        onUpdate(response);
        dispatchCloseModalAction();
      } else {
        this.setState({ persisting: false });
      }
    }
  };

  handleToggleInfoVisibility = (info: I.Info) => {
    this.setState((prevState) => {
      let newVisiblePropertyIds = [...prevState.visiblePropertyIds];

      if (newVisiblePropertyIds.includes(info.id)) {
        newVisiblePropertyIds = newVisiblePropertyIds.filter((id) => id !== info.id);
      } else {
        newVisiblePropertyIds.push(info.id);
      }

      return {
        visiblePropertyIds: newVisiblePropertyIds,
      };
    });
  };

  handleToggleGroupVisibility = (group: I.Group) => {
    this.setState((prevState) => {
      let newVisiblePropertyIds = [...prevState.visiblePropertyIds];
      const ids = group.infos.map((info) => info.id);
      const selectedAll = hasGroupSelectedAll(group, prevState.visiblePropertyIds);
      const disabledProperty = this.getDisabledProperty();

      if (selectedAll) {
        newVisiblePropertyIds = newVisiblePropertyIds.filter((id) => !ids.includes(id) || id === disabledProperty?.id);
      } else {
        newVisiblePropertyIds = arrayUnique([...newVisiblePropertyIds, ...ids]);
      }

      return {
        visiblePropertyIds: newVisiblePropertyIds,
      };
    });
  };

  handleResetGroupVisibility = (group: I.Group) => {
    this.setState((prevState) => {
      const ids = group.infos.map((info) => info.id);

      const newVisiblePropertyIds = prevState.visiblePropertyIds.filter((id) => !ids.includes(id));

      ids.forEach((id) => {
        if (prevState.initialVisiblePropertyIds.includes(id)) {
          newVisiblePropertyIds.push(id);
        }
      });

      return {
        selectedSet: undefined,
        visiblePropertyIds: newVisiblePropertyIds,
      };
    });
  };

  handleResetFiltered = () => {
    const { metaInfo } = this.props;
    const { filterGroup, filterQuery } = this.state;

    if (filterGroup || filterQuery.length > 0) {
      const ids = filterGroupInfosWithQueryAndReturnMatchingIds(metaInfo, filterGroup, filterQuery);

      this.setState((prevState) => {
        const idsToAdd: string[] = [];
        const idsToDelete: string[] = [];

        ids.forEach((id) => {
          if (prevState.initialVisiblePropertyIds.includes(id)) {
            idsToAdd.push(id);
          } else {
            idsToDelete.push(id);
          }
        });

        let newVisiblePropertyIds = arrayUnique([...prevState.visiblePropertyIds, ...idsToAdd]);
        newVisiblePropertyIds = arrayFilterOut(newVisiblePropertyIds, idsToDelete);

        return {
          ...prevState,
          selectedSet: undefined,
          visiblePropertyIds: newVisiblePropertyIds,
        };
      });
    } else {
      this.handleResetSelection();
    }
  };

  handleSetChange = (id?: string) => {
    const { columnVisibilitySettings: { availableColumnVisibilitySets } } = this.props;
    const selectedSet = availableColumnVisibilitySets.find((set) => set.id.toString() === id);

    if (selectedSet) {
      this.setState({
        saveAsExpanded: 'update',
        selectedSet,
        initialVisiblePropertyIds: selectedSet.visiblePropertyIds,
        visiblePropertyIds: selectedSet.visiblePropertyIds,
      });
    } else {
      this.setState({
        saveAsExpanded: undefined,
      });
    }
  };

  handleSelectAll = () => {
    const { metaInfo } = this.props;

    const ids = metaInfoInfos({ groups: metaInfo }).map((info) => info.id);

    this.setState({
      visiblePropertyIds: ids,
    });
  };

  handleSelectAllFiltered = () => {
    const { metaInfo } = this.props;
    const { filterGroup, filterQuery } = this.state;

    if (filterGroup || filterQuery.length > 0) {
      const ids = filterGroupInfosWithQueryAndReturnMatchingIds(metaInfo, filterGroup, filterQuery);

      this.setState((prevState) => ({
        ...prevState,
        visiblePropertyIds: arrayUnique([...prevState.visiblePropertyIds, ...ids]),
      }));
    } else {
      this.handleSelectAll();
    }
  };

  handleSelectNone = () => {
    const { metaInfo } = this.props;
    // Ensure we never deselect the first property
    const ids = [metaInfo[0].infos[0].id];

    this.setState({
      visiblePropertyIds: ids,
    });
  };

  handleSelectNoneFiltered = () => {
    const { metaInfo } = this.props;
    const { filterGroup, filterQuery } = this.state;

    if (filterGroup || filterQuery.length > 0) {
      let ids = filterGroupInfosWithQueryAndReturnMatchingIds(metaInfo, filterGroup, filterQuery);

      // Ensure we never deselect the first property
      ids = ids.filter((id) => id !== metaInfo[0].infos[0].id);

      this.setState((prevState) => ({
        ...prevState,
        visiblePropertyIds: arrayFilterOut(prevState.visiblePropertyIds, ids),
      }));
    } else {
      this.handleSelectNone();
    }
  };

  handleResetSelection = () => {
    this.setSelectedSet();
  };

  handleDeleteSelectedSet = async () => {
    const {
      dispatchDeleteCommand, onUpdate,
      dispatchEnqueueInfoNotification,
    } = this.props;
    const { selectedSet } = this.state;

    if (selectedSet && selectedSet.deleteCommand) {
      const response = await dispatchDeleteCommand(selectedSet.deleteCommand);

      if (isSectionResponse(response)) {
        dispatchEnqueueInfoNotification(I18n.Elements.Table.ModalColsVisibility.deletedSuccess);

        this.setState({
          // Remove the selected set but keep existing properties so the user
          // can decide what is the next step
          selectedSet: undefined,
        });

        onUpdate(response);
      }
    }
  };

  setSelectedSet = () => {
    const { visiblePropertyIds, columnVisibilitySettings: { availableColumnVisibilitySets } } = this.props;

    this.setState((prevState) => {
      const newState: ComponentState = { ...prevState, selectedSet: undefined, visiblePropertyIds };

      if (availableColumnVisibilitySets && availableColumnVisibilitySets.length > 0) {
        availableColumnVisibilitySets.forEach((set) => {
          const ids = set.visiblePropertyIds;
          if (arrayEquals(ids, visiblePropertyIds)) {
            newState.selectedSet = set;
            newState.visiblePropertyIds = ids;
            newState.saveAsExpanded = 'update';
          }
        });
      }

      return newState;
    });
  };

  /**
   * Corresponds usually to the record's unique identifier.
   * Before SPA ids could be disabled. We need to allow the user to re-enable the ID ONLY if it was disabled before.
   */
  getDisabledProperty = () => {
    const { metaInfo } = this.props;
    const { visiblePropertyIds } = this.state;

    const info = metaInfo[0].infos[0];
    return !visiblePropertyIds.includes(info.id) ? undefined : info;
  };

  render() {
    const {
      subHeader, metaInfo, maxShownColumns,
      columnVisibilitySettings: {
        canCreateSharedSettings, availableColumnVisibilitySets,
      },
    } = this.props;
    const {
      filterQuery, newSettingName, newSettingShared, visiblePropertyIds, touchedPropertyIds, selectedSet,
      saveAsExpanded, persisting, filterGroup,
    } = this.state;

    const disabledProperty = this.getDisabledProperty();

    const {
      groups: filteredMetaInfo, propertiesCount, maxShownColumnsReached,
      unshownVisiblePropertiesCount, unshownPropertyIdsCount,
    } = filterGroupInfos(
      metaInfo, visiblePropertyIds, filterGroup, filterQuery, maxShownColumns,
    );

    const disableApply = persisting || (saveAsExpanded === 'new' && newSettingName.length === 0);

    return (
      <Box data-testid="ModalColsVisibility">
        <Typography>{subHeader}</Typography>

        <Box display="flex" mt={1}>
          <Box flexGrow="1">
            <InputSelect
              outlined
              value={selectedSet ? selectedSet.id.toString() : DEFAULT_SETTING_ID}
              options={availableColumnVisibilitySetsAsOptions(availableColumnVisibilitySets)}
              optionsGroups={availableColumnVisibilitySetsAsOptionsGroups(availableColumnVisibilitySets)}
              onChange={this.handleSetChange}
              neverEmpty
              e2eId="settingsList"
              disabled={saveAsExpanded === 'new'}
            />
          </Box>
          <Box alignSelf="center">
            <Button
              onClick={this.handleResetSelection}
              action="secondary"
              ml
              title={I18n.Elements.Table.ModalColsVisibility.resetTitle}
            >
              {I18n.Elements.Table.ModalColsVisibility.reset}
            </Button>
          </Box>
        </Box>
        <Box mt={1} mb={1}>
          <Divider />
        </Box>
        <Box mb={1}>
          <Box textAlign="center">
            <Typography variant="body2">
              {propertiesCount > maxShownColumns && (
                <>
                  <Icon element={InfoIcon} mr />
                  {I18n.Elements.Table.ModalColsVisibility.maxShownColumnsInfo(
                    visiblePropertyIds.length, maxShownColumns,
                  )}
                  <br />
                </>
              )}
            </Typography>
            <Typography variant="body2" style={{ color: ThemeColors.softWarning }}>
              {maxShownColumnsReached && (
                <>
                  {I18n.Elements.Table.ModalColsVisibility.filteredOutInfo(
                    unshownPropertyIdsCount, unshownVisiblePropertiesCount,
                  )}
                  .
                  {I18n.Elements.Table.ModalColsVisibility.refineFurther(filterQuery.length > 0)}
                </>
              )}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            {metaInfo.length > 1 && (
              <Box minWidth="200px" mr={1}>
                <FilterGroupSelect
                  groups={metaInfo}
                  filterGroup={filterGroup}
                  onUpdate={(newFilterGroup) => this.setState({ filterGroup: newFilterGroup })}
                />
              </Box>
            )}
            <Box flexGrow={1} mt="2px">
              <DebouncedInputText
                placeholder={I18n.Elements.Table.ModalColsVisibility.filterPlaceholder}
                value={filterQuery}
                onChange={(val) => {
                  this.setState({ filterQuery: val });
                }}
                clearable
                e2eId="filterQuery"
                fullWidth
              />
            </Box>
            <Box>
              <Button
                small
                onClick={this.handleSelectAllFiltered}
                action="secondary"
                ml
                title={I18n.Elements.Table.ModalColsVisibility.allFilteredTitle}
              >
                {I18n.Elements.Table.ModalColsVisibility.allFiltered}
              </Button>
              <Button
                small
                onClick={this.handleSelectNoneFiltered}
                action="secondary"
                ml
                title={I18n.Elements.Table.ModalColsVisibility.noneFilteredTitle}
              >
                {I18n.Elements.Table.ModalColsVisibility.noneFiltered}
              </Button>
              <Button
                small
                onClick={this.handleResetFiltered}
                action="secondary"
                ml
                title={I18n.Elements.Table.ModalColsVisibility.resetFilteredTitle}
              >
                {I18n.Elements.Table.ModalColsVisibility.resetFiltered}
              </Button>
            </Box>
          </Box>
        </Box>

        <ScrollableModalContent>
          <>
            {filteredMetaInfo.map((filteredGroup, index) => (
              <Box
                key={`colsVisibility-${filteredGroup.id}`}
                p={metaInfo.length > 1 ? theme.spacing(0, 1, 0.5, 1) : 0}
                style={{ backgroundColor: index % 2 === 0 ? 'inherit' : ThemeColors.contentRowOther }}
              >
                <Group
                  filteredGroup={filteredGroup}
                  group={metaInfo.find((i) => i.id === filteredGroup.id) as I.Group}
                  visiblePropertyIds={visiblePropertyIds}
                  touchedPropertyIds={touchedPropertyIds}
                  onToggleInfo={this.handleToggleInfoVisibility}
                  onToggleGroup={this.handleToggleGroupVisibility}
                  onResetGroup={this.handleResetGroupVisibility}
                  showGroupToggle={metaInfo.length > 1 && !filterGroup}
                  disabledProperty={disabledProperty}
                  onOpen={
                    filterGroup ? undefined : () => this.setState({ filterGroup: filteredGroup, filterQuery: '' })
                  }
                />
              </Box>
            ))}
          </>
        </ScrollableModalContent>

        <ModalActions
          onApply={this.handleApply}
          applyText={applyText(saveAsExpanded, selectedSet)}
          persisting={persisting}
          disableApply={disableApply}
          leftActions={saveAsExpanded !== 'new' ? (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Button
                action="secondary"
                onClick={() => this.setState({ saveAsExpanded: 'new' })}
              >
                {I18n.Elements.Table.ModalColsVisibility.saveAs}
              </Button>
              <Button
                action="tertiary"
                disabled={!selectedSet?.deleteCommand}
                onClick={this.handleDeleteSelectedSet}
                ml
              >
                {I18n.Elements.Table.ModalColsVisibility.delete}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <InputText
                name="newSettingName"
                autoComplete="on"
                label={I18n.Elements.Table.ModalColsVisibility.enterName}
                outlined
                value={newSettingName}
                onChange={this.handleNewSettingNameChange}
                e2eId="newSettingName"
              />
              {canCreateSharedSettings && (
                <Box ml={2} mr={1}>
                  <InputCheckbox
                    label={I18n.Elements.Table.ModalColsVisibility.shared}
                    title={I18n.Elements.Table.ModalColsVisibility.sharedTitle}
                    checked={newSettingShared}
                    onChange={this.handleNewSettingSharedChange}
                    e2eId="newSettingShared"
                    small
                  />
                </Box>
              )}
              <Button
                action="tertiary"
                ml
                onClick={() => this.setState({ saveAsExpanded: selectedSet ? 'update' : undefined })}
              >
                {I18n.Elements.Table.ModalColsVisibility.cancel}
              </Button>
            </Box>
          )}
        />
      </Box>
    );
  }
}

const mapDispatchToProps = (
  dispatch: AppThunkDispatch,
) => ({
  dispatchEnqueueInfoNotification: (message: string) => (
    (dispatch as AppDispatch)(enqueueInfoNotification(message))
  ),
  dispatchUpdateCommand: (command: I.Command, data: Parameters<UpdateTableColumnsVisibilityCommand>[1]) => (
    dispatch(updateCommand<Parameters<UpdateTableColumnsVisibilityCommand>[1]>(command, data))
  ),
  dispatchSaveCommand: (command: I.Command, data: SaveCommandData) => (
    dispatch(saveCommand(command, data))
  ),
  dispatchDeleteCommand: (command: I.Command) => (
    dispatch(deleteCommand(command))
  ),
  dispatchCloseModalAction: () => (dispatch as AppDispatch)(closeModalAction()),
});

export default connect(
  null,
  mapDispatchToProps,
)(ModalColsVisibility);
